import React, { useState } from "react";
import {
  Card,
  Grid,
  Box,
  Typography,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import Iconify from "../iconify/Iconify";
import { fDate } from "src/utils/formatTime";
import { fCoins } from "src/utils/formatNumber";

const UserProperty = ({
  name,
  value,
  isEditable,
  onValueChange,
  onEdit,
  icon,
  minWidth = "100px",
  isLoading = false,
  rightIcon,              // optional icon name (e.g., "eva:trash-2-outline")
  onRightIconClick,       // optional click handler for right icon
  rightIconLoading = false,
  rightIconDisabled = false,
  rightIconAriaLabel = "action",
  rightIconTitle,         // optional tooltip/title
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [editing, setEditing] = useState(false);

  // keep input in sync if parent updates the value
  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleInputChange = (e) => {
    setLocalValue(e.target.value);
    if (onValueChange) {
      onValueChange(e.target.value);
    }
  };

  const handleEditClick = () => {
    if (editing) {
      if (onEdit) {
        onEdit(localValue);
      }
    }
    setEditing(!editing);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 2 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: "bold", minWidth: minWidth, fontSize: "0.875rem" }}
      >
        {name}
      </Typography>
      <TextField
        value={localValue ?? ""}
        onChange={handleInputChange}
        disabled={!editing}
        variant="outlined"
        size="small"
        InputProps={
          icon
            ? {
              startAdornment: (
                <Box
                  component="img"
                  src={icon}
                  sx={{
                    width: 20,
                    height: 20,
                    marginRight: 1,
                  }}
                  alt=""
                />
              ),
            }
            : {}
        }
        sx={{
          flexGrow: 1,
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
          },
        }}
      />
      {isEditable && (
        <IconButton onClick={handleEditClick} size="small" disabled={isLoading}>
          <Iconify icon={isLoading ? "eos-icons:loading" : editing ? "eva:save-outline" : "eva:edit-outline"} />
        </IconButton>
      )}
      {rightIcon && (
        <IconButton
          onClick={onRightIconClick}
          size="small"
          disabled={rightIconDisabled || rightIconLoading}
          aria-label={rightIconAriaLabel}
          title={rightIconTitle}
        >
          <Iconify icon={rightIconLoading ? "eos-icons:loading" : rightIcon} />
        </IconButton>
      )}
    </Box>
  );
};

export default function ProfileUserData({
  user,
  userLevelStats,
  userLevel,
  onSaveField,
}) {
  const [loadingFields, setLoadingFields] = useState({});

  const stopLoading = (field) =>
    setLoadingFields((p) => ({ ...p, [field]: false }));

  React.useEffect(() => {
    if (loadingFields.deviceId && user?.deviceId == null) {
      stopLoading("deviceId");
    }
  }, [user?.deviceId, loadingFields.deviceId]);

  const handleClearDeviceId = () => {
    if (!onSaveField) return;
    // start spinner immediately
    setLoadingFields((p) => ({ ...p, deviceId: true }));
    try {
      const r = onSaveField("deviceId", null);
      // if it's a Promise, stop spinner on failure
      if (r && typeof r.then === "function" && typeof r.catch === "function") {
        r.catch(() => stopLoading("deviceId"));
      }
    } catch {
      // synchronous throw -> stop spinner
      stopLoading("deviceId");
    }
  };

  const calculateAccountAge = (createdAt) => {
    if (!createdAt) return "N/A";

    const now = new Date();
    const createdDate = new Date(createdAt);
    const diffInMs = now - createdDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    return `${diffInDays} days`;
  };

  const handleValueChange = (field, value) => {
    console.log(`${field} changed to:`, value);
  };

  const handleEdit = (field, value) => {
    if (onSaveField) {
      setLoadingFields(prev => ({ ...prev, [field]: true }));
      try {
        const result = onSaveField(field, value);
        if (result && typeof result.finally === 'function') {
          result.finally(() => {
            setLoadingFields(prev => ({ ...prev, [field]: false }));
          });
        } else {
          // If onSaveField doesn't return a promise, clear loading after a short delay
          setTimeout(() => {
            setLoadingFields(prev => ({ ...prev, [field]: false }));
          }, 500);
        }
      } catch (error) {
        setLoadingFields(prev => ({ ...prev, [field]: false }));
      }
    }
  };

  const handleAddressEdit = (addressField, value) => {
    if (!onSaveField) return;
    const loadingKey = `address.${addressField}`;
    setLoadingFields((prev) => ({ ...prev, [loadingKey]: true }));
    const updatedAddress = { ...(user?.address || {}), [addressField]: value };
    try {
      const result = onSaveField("address", updatedAddress);
      if (result && typeof result.finally === "function") {
        result.finally(() => setLoadingFields((prev) => ({ ...prev, [loadingKey]: false })));
      } else {
        setTimeout(() => setLoadingFields((prev) => ({ ...prev, [loadingKey]: false })), 500);
      }
    } catch {
      setLoadingFields((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleChangeField = (field, value) => {
    handleValueChange(field, value);
  
    if (!onSaveField) return;
  
    // Set loading for this field
    setLoadingFields(prev => ({ ...prev, [field]: true }));
  
    try {
      const result = onSaveField(field, value);
  
      if (result && typeof result.finally === "function") {
        result.finally(() => {
          setLoadingFields(prev => ({ ...prev, [field]: false }));
        });
      } else {
        // If not a promise, clear loading after short delay
        setTimeout(() => {
          setLoadingFields(prev => ({ ...prev, [field]: false }));
        }, 500);
      }
    } catch (error) {
      setLoadingFields(prev => ({ ...prev, [field]: false }));
    }
  };

  return (
    <Card sx={{ mb: 2, p: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={4}>
          <UserProperty
            name="User ID"
            value={user.userId}
            isEditable={false}
            onValueChange={() => { }}
            onEdit={() => { }}
          />
          <UserProperty
            name="Username"
            value={user.username}
            isEditable={true}
            onValueChange={(value) => handleValueChange("username", value)}
            onEdit={(value) => handleEdit("username", value)}
            isLoading={loadingFields.username}
          />
          <UserProperty
            name="First Name"
            value={user.firstName}
            isEditable={true}
            onValueChange={(value) => handleValueChange("firstName", value)}
            onEdit={(value) => handleEdit("firstName", value)}
            isLoading={loadingFields.firstName}
          />
          <UserProperty
            name="Last Name"
            value={user.lastName}
            isEditable={true}
            onValueChange={(value) => handleValueChange("lastName", value)}
            onEdit={(value) => handleEdit("lastName", value)}
            isLoading={loadingFields.lastName}
          />
          <UserProperty
            name="Mobile Number"
            value={user.mobileNumber}
            isEditable={true}
            onValueChange={(value) =>
              handleValueChange("mobileNumber", value)
            }
            onEdit={(value) => handleEdit("mobileNumber", value)}
            isLoading={loadingFields.mobileNumber}
          />
          <UserProperty
            name="Birth Date"
            value={fDate(user.birthDate) ?? "N/A"}
            isEditable={false}
          />
          <UserProperty
            name="Division"
            value={user?.divisionLeague?.currentDivisionName ?? "N/A"}
            isEditable={false}
          />
          <UserProperty
            name="Country"
            value={user.address?.country || "N/A"}
            isEditable={true}
            onValueChange={(value) => handleValueChange("address.country", value)}
            onEdit={(value) => handleAddressEdit("country", value)}
            isLoading={loadingFields["address.country"]}
          />
        </Grid>
        <Grid item xs={4}>
          <UserProperty
            name="Email"
            value={user.email}
            isEditable={true}
            onValueChange={(value) => handleValueChange("email", value)}
            onEdit={(value) => handleEdit("email", value)}
            isLoading={loadingFields.email}
          />
          <UserProperty
            name="Addit. Email"
            value={user.additionalEmail}
            isEditable={true}
            onValueChange={(value) =>
              handleValueChange("additionalEmail", value)
            }
            onEdit={(value) => handleEdit("additionalEmail", value)}
            isLoading={loadingFields.additionalEmail}
          />
          <UserProperty
            name="Age of Account"
            value={calculateAccountAge(user.createdAt)}
            isEditable={false}
          />
          <UserProperty
            name="Coin Balance"
            value={fCoins(user.coinsBalance)}
            isEditable={false}
            onValueChange={() => { }}
            onEdit={() => { }}
            icon="/assets/icons/ufcoin.svg"
          />
          <UserProperty
            name="Device ID"
            value={user.deviceId}
            isEditable={false}
            rightIcon={"eva:trash-2-outline"}
            rightIconTitle="Remove device id"
            rightIconAriaLabel="Remove device id"
            rightIconDisabled={!user.deviceId}
            rightIconLoading={!!loadingFields.deviceId}
            onRightIconClick={handleClearDeviceId}
          />
          <UserProperty
            name="Updated At"
            value={fDate(user.updatedAt)}
            isEditable={false}
            onValueChange={() => { }}
            onEdit={() => { }}
          />          
          <UserProperty
            name="Province / State"
            value={user.address?.state || "N/A"}
            isEditable={true}
            onValueChange={(value) => handleValueChange("address.state", value)}
            onEdit={(value) => handleAddressEdit("state", value)}
            isLoading={loadingFields["address.state"]}
          />

          {/* only show attributes if nhl specific league */}
          {(process.env.REACT_APP_CLIENT_NAME === "NHL" ||
            process.env.REACT_APP_CLIENT_NAME === "nhldev") && (
              <>
                <UserProperty
                  name="NHL Gamezone User ID"
                  value={user.nhlGamezoneUserId || "N/A"}
                  isEditable={false}
                />

                <UserProperty
                  name="NHL Account ID"
                  value={user.nhlAccountId || "N/A"}
                  isEditable={false}
                />
              </>
            )}
        </Grid>
        <Grid item xs={4}>
          <UserProperty
            name="Referral Code"
            value={user.referralCode}
            isEditable={false}
          />
          <UserProperty
            name="Referrals"
            value={user.referrals}
            isEditable={false}
          />
          <UserProperty
            name="Days Until First Purchase"
            value={user.daysUntilFirstPurchase}
            isEditable={false}
            onValueChange={() => { }}
            onEdit={() => { }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <UserProperty
                name="XP Level"
                value={user.xpDetails.level.name || "N/A"}
                isEditable={false}
                icon="/assets/icons/xp-level-badge.svg"
                minWidth="60px"
              />
            </Grid>
            <Grid item xs={6}>
              <UserProperty
                name="Pts"
                value={fCoins(user.xpDetails.totalRewards)}
                isEditable={false}
                icon="/assets/icons/xp-level-badge.svg"
                minWidth="20px"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={user.isEnabled}
                      onChange={(e) => handleChangeField("isEnabled", e.target.checked)}
                      id="isEnabled"
                      disabled={loadingFields.isEnabled}
                    />
                    {loadingFields.isEnabled && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                  </Box>
                }
                label="Is Enabled"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={user.isTester}
                      onChange={(e) => handleChangeField("isTester", e.target.checked)}
                      id="isTester"
                      disabled={loadingFields.isTester}
                    />
                    {loadingFields.isTester && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                  </Box>
                }
                label="Is Tester"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={user.isKycDone}
                      onChange={(e) => handleChangeField("isKycDone", e.target.checked)}
                      id="isKycDone"
                      disabled={loadingFields.isKycDone}
                    />
                    {loadingFields.isKycDone && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                  </Box>
                }
                label="Is KYC Done"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
}
